import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { DomainErrorBoundary } from "@/core/components/DomainErrorBoundary";
import useCursor from "@/core/helpers/useCursor";
import { useKeyPress } from "@/core/keyPressManager";
import { useGlobalNotificationSubscription } from "@/stores/notifications/useGlobalNotificationSubscription";
import DevContainer from "@/domains/dev/containers/DevContainer";
import TargetMenuContainer from "@/domains/targetMenu/containers/TargetMenuContainer";
import useUpdatePlayerInfo from "@/domains/main/hooks/useUpdatePlayerInfo";
import AccountContainer from "@/domains/account/containers/AccountContainer";
import AuthContainer from "@/domains/auth/containers/AuthContainer";
import { BankATMContainer } from "@/domains/bank/containers/atm/BankATMContainer";
import { BankBranchContainer } from "@/domains/bank/containers/branch/BankBranchContainer";
import BusinessContainer from "@/domains/business/containers/BusinessContainer";
import ChatContainer from "@/domains/chat/containers/chat/ChatContainer";
import CreateCharacterContainer from "@/domains/createCharacter/containers/CreateCharacterContainer";
import CreateEntityContainer from "@/domains/createEntity/containers/CreateEntityContainer";
import DeathScreenContainer from "@/domains/deathScreen/containers/DeathScreenContainer";
import DialogueContainer from "@/domains/dialogue/containers/DialogueContainer";
import DocumentsContainer from "@/domains/documents/containers/DocumentsContainer";
import EstateContainer from "@/domains/estate/containers/EstateContainer";
import GasStationContainer from "@/domains/gasStation/containers/GasStationContainer";
import HotelContainer from "@/domains/hotel/containers/HotelContainer";
import HudContainer from "@/domains/hud/containers/HudContainer";
import InventoryContainer from "@/domains/inventory/containers/InventoryContainer";
import LoadingScreenContainer from "@/domains/loadingScreen/containers/LoadingScreenContainer.tsx";
import NotificationContainer from "@/domains/notification/containers/NotificationContainer";
import OfferContainer from "@/domains/offer/containers/OfferContainer.tsx";
import PhoneContainer from "@/domains/phone/containers/PhoneContainer";
import PlayerMenuContainer from "@/domains/playerMenu/containers/PlayerMenuContainer";
import ShopContainer from "@/domains/shop/containers/ShopContainer";
import SpawnContainer from "@/domains/spawn/containers/SpawnContainer";
import TabletContainer from "@/domains/tablet/containers/TabletContainer";
import VehicleSpawnContainer from "@/domains/vehicleSpawn/containers/VehicleSpawnContainer";
import KeyCode from "@/assets/constants/key-code";

const queryClient = new QueryClient();

export default function MainContainer() {
  const [showCreateEntity, setCreateEntity] = useState(false);
  const { subscribeKeyDown, unsubscribeKeyDown } = useKeyPress();
  const { hideCursor, showCursor } = useCursor();

  useGlobalNotificationSubscription();
  useUpdatePlayerInfo();

  const keyDownHandler = useCallback((keyCode: KeyCode) => {
    switch (keyCode) {
      case KeyCode.F12: {
        setCreateEntity((state) => {
          if (state) {
            hideCursor();
          } else {
            showCursor();
          }

          return !state;
        });

        break;
      }
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const key = "devContainer";
    subscribeKeyDown(key, keyDownHandler);

    return () => {
      unsubscribeKeyDown(key);
    };
  }, [subscribeKeyDown, unsubscribeKeyDown, keyDownHandler]);

  return (
    <>
      <Outlet />

      <DomainErrorBoundary domainName="Dev Tools">
        <DevContainer />
      </DomainErrorBoundary>

      <AuthContainer />
      <AccountContainer />
      <CreateCharacterContainer />
      <ChatContainer />
      <DialogueContainer />
      <InventoryContainer />
      <TargetMenuContainer />
      <SpawnContainer />

      <NotificationContainer />

      <EstateContainer />
      <DeathScreenContainer />
      <GasStationContainer />
      <LoadingScreenContainer />
      <OfferContainer />
      <CreateEntityContainer isOpen={showCreateEntity} />

      <QueryClientProvider client={queryClient}>
        <DomainErrorBoundary domainName="HUD">
          <HudContainer />
        </DomainErrorBoundary>

        <DomainErrorBoundary domainName="Vehicle Spawn">
          <VehicleSpawnContainer />
        </DomainErrorBoundary>

        <TabletContainer />
        <PhoneContainer />

        <DomainErrorBoundary domainName="Bank Branch">
          <BankBranchContainer />
        </DomainErrorBoundary>

        <DomainErrorBoundary domainName="Bank ATM">
          <BankATMContainer />
        </DomainErrorBoundary>

        <DomainErrorBoundary domainName="Shop">
          <ShopContainer />
        </DomainErrorBoundary>
      </QueryClientProvider>

      <PlayerMenuContainer />
      <HotelContainer />
      <BusinessContainer />
      <DocumentsContainer />
    </>
  );
}
